/**
 * Container for ArtistEventsItem
 *
 * @module components/ArtistEvents/ArtistEventsItem/ArtistEventsItem
 */
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedTime } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import url from 'url';

import ArtistEventRowItem from '../../../components/lib/ArtistEventsRowItem/ArtistEventRowItem';
import { ArtistEventsConstants } from '../../../constants/ArtistEvents';
import { getInDomain } from '../../../domains/externalUtils';
import { isValidDate, parseDateString } from '../../../lib/DateUtils';
import { getCurrentDominantColor } from '../../../lib/UIStateUtils';
import { createBEM } from '../../../lib/utils/css';
import * as Utils from '../../../utils';
import { withVisibilityLogger } from '../../VisibilityLogger/VisibilityLogger';

import './ArtistEventsItem.scss';

const ArtistEventRowItemWithLogger = withVisibilityLogger(null, {
  useMemo: true,
})(ArtistEventRowItem);

export class ArtistEventsItem extends React.Component {
  static propTypes = {
    eventId: PropTypes.string.isRequired,
    artistPandoraId: PropTypes.string,
    isNowPlaying: PropTypes.bool,
    isOnDemandNowPlaying: PropTypes.bool,
    isRowItemArtistEvents: PropTypes.bool,
    date: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    venueName: PropTypes.string,
    url: PropTypes.string,
    notificationId: PropTypes.string,
    logConcertNotificationClicked: PropTypes.func.isRequired,
    logConcertNotificationSent: PropTypes.func.isRequired,
    logConcertNotificationNoLongerOnPage: PropTypes.func.isRequired,
    logConcertNotificationViewed: PropTypes.func.isRequired,
    getPseudoRandom: PropTypes.func.isRequired,
    useDarkTheme: PropTypes.bool,
    useLightTheme: PropTypes.bool,
    buttonT3TextColor: PropTypes.string,
    artistMusicId: PropTypes.string,
    noBorder: PropTypes.bool,
  };

  componentDidMount() {
    this.props.logConcertNotificationSent(this.getConcertNotificationArgs());
  }

  componentWillUnmount() {
    this.props.logConcertNotificationNoLongerOnPage(this.props.eventId);
  }

  addQueryParams = cleanUrl => {
    const obj = url.parse(cleanUrl, true, false);
    obj.query[ArtistEventsConstants.UTM_MEDIUM] = this.props.isNowPlaying
      ? ArtistEventsConstants.NOW_PLAYING_PARAM
      : ArtistEventsConstants.ARTIST_PAGES_PARAM;
    delete obj.search; // this makes format compose the search string out of the query object
    const urlWithParams = url.format(obj);
    return urlWithParams;
  };

  onButtonClick = event => {
    event.stopPropagation();
    const { actionLink, eventId, artistId, notificationId } = this.getConcertNotificationArgs();
    this.props.logConcertNotificationClicked({
      actionLink,
      eventId,
      artistId,
      notificationId,
    });
    window.open(actionLink).focus();
  };

  getNotificationId = () => {
    if (!this._notificationId) {
      this._notificationId = this.props.getPseudoRandom();
    }
    return this._notificationId;
  };

  getConcertNotificationArgs = () => {
    const { eventId, artistMusicId, artistPandoraId } = this.props;

    return {
      eventId,
      actionLink: this.props.url,
      artistId: artistMusicId || artistPandoraId,
      notificationId: this.getNotificationId(),
    };
  };

  onComponentInView = () => {
    this.props.logConcertNotificationViewed(this.getConcertNotificationArgs());
  };

  renderSecondText = () => {
    const secondTextArray = [this.props.venueName];
    const date = parseDateString(this.props.date);
    const timeFormat = { hour: '2-digit' };
    if (date.getMinutes() !== 0) {
      timeFormat.minute = '2-digit';
    }

    if (isValidDate(date)) {
      secondTextArray.push(' ', <FormattedTime value={date} {...timeFormat} />);
    }

    return secondTextArray.map((text, i) => <React.Fragment key={text + i}>{text}</React.Fragment>);
  };

  renderArtistEventRowItem = () => {
    const date = new Date(this.props.date);
    const mainText = [this.props.city, this.props.state].filter(val => val).join(', ');

    return (
      <ArtistEventRowItemWithLogger
        onComponentInView={this.onComponentInView}
        date={date}
        mainText={mainText}
        secondText={this.renderSecondText()}
        isRowSmall={this.props.isNowPlaying}
        isNowPlaying={this.props.isNowPlaying}
        noBorder={this.props.noBorder}
        buttonT3TextColor={this.props.buttonT3TextColor}
        onButtonClick={this.onButtonClick}
        useDarkTheme={this.props.useDarkTheme}
        useLightTheme={this.props.useLightTheme}
      />
    );
  };

  render() {
    if (this.props.isRowItemArtistEvents) {
      return this.renderArtistEventRowItem();
    }

    const itemClass = createBEM('ArtistEventsItem', {
      nowPlaying: this.props.isNowPlaying,
    });

    return <div className={itemClass}>{this.renderArtistEventRowItem()}</div>;
  }
}

function getDate(dateProp) {
  if (isString(dateProp)) {
    return dateProp;
  }
  if (isObject(dateProp)) {
    const { year, month, day } = dateProp.get('date');
    return new Date(year, month, day).toDateString();
  }
  return '';
}

function getEventData(state, ownProps) {
  const props = {};
  let content;
  if (ownProps.artistPandoraId) {
    const events = getInDomain('catalog.utils.getArtistEventsFromState')(
      state,
      ownProps.artistPandoraId,
    );
    content = events.find(candidate => {
      return candidate.get('id') === ownProps.eventId;
    });
  }

  if (content) {
    props.date = getDate(content.get('date'));
    props.city = content.get('city');
    props.state = content.get('state') || '';
    props.venueName = content.get('venueName');
    props.url = content.get('url');
  }
  return props;
}

export function mapStateToProps(state, ownProps) {
  const props = getEventData(state, ownProps);
  props.getPseudoRandom = Utils.NumberUtils.getPseudoRandom;

  const isNowPlaying = ownProps.isNowPlaying;
  if (isNowPlaying) {
    props.buttonT3TextColor = getCurrentDominantColor(state);
  }
  return props;
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      logConcertNotificationClicked: getInDomain(
        'analytics.actions.logConcertNotificationClicked',
        noop,
      ),
      logConcertNotificationSent: getInDomain('analytics.actions.logConcertNotificationSent', noop),
      logConcertNotificationNoLongerOnPage: getInDomain(
        'analytics.actions.logConcertNotificationNoLongerOnPage',
        noop,
      ),
      logConcertNotificationViewed: getInDomain(
        'analytics.actions.logConcertNotificationViewed',
        noop,
      ),
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ArtistEventsItem);
